import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import {
  useListTaskTemplates,
  useCreateTaskTemplate,
  useUpdateTaskTemplate,
  useDeleteTaskTemplate,
  usePermanentlyDeleteTaskTemplate,
  useCreateTaskTemplateItem,
  useUpdateTaskTemplateItem,
  useDeleteTaskTemplateItem,
  useReorderTaskTemplateItem,
  useSyncTaskTemplateToday,
  useListSpaceResources,
  useListUsers,
  getListTaskTemplatesQueryKey,
  getListUsersQueryKey,
  getGetDailyChecklistQueryKey,
} from "@workspace/api-client-react";
import type { TaskTemplateDetail, TaskTemplateItem, TaskPriority, User } from "@workspace/api-client-react";
import { useAuth } from "@/context/auth-context";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter, SheetDescription } from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandItem, CommandEmpty, CommandGroup } from "@/components/ui/command";
import { ListChecks, ShieldAlert, ArrowUp, ArrowDown, Pencil, Trash2, Check, ChevronsUpDown, UserRound } from "lucide-react";

// Phase 43 Plan 06 (TREC-03/TREC-10) — Settings > Checklists editor. Mirrors
// artifacts/crm/src/pages/tasks/settings/routing-settings.tsx's Table+Dialog
// CRUD shape + ArrowUp/ArrowDown reorder (no @dnd-kit, per plan). Beyond
// routing_rule this page nests a SECOND CRUD surface (checklist items)
// inside the Edit Checklist dialog — a "table-in-dialog" with its own
// Add/Edit item Dialog stacked on top (independent open-state, same
// sibling-Dialog convention categories-settings.tsx already uses for its
// "Copy config" dialog next to the Edit dialog).
//
// admin + ops_lead gated (43-CONTEXT.md "Config / edit UX" — canEditConfig
// server-side); GET /task-templates itself is open to any authenticated
// role (matches routing-settings.tsx's GET-is-open convention), so `canEdit`
// below only gates whether this page renders the CRUD UI.
//
// Editing a checklist NEVER mutates today's already-spawned round (TREC-07 —
// every template/item write bumps task_template.version; spawned
// task_checklist_item rows are copies, never a live join). Deactivating
// (Archive) is non-destructive (TREC-10) — it only stops future spawns.
//
// Phase 49 Plan 09 (TEVT-01/TASN-01/TASN-02) — extends this SAME editor
// (no new page) with:
//   1. A top-level Trigger select (Daily/Weekly/Monthly/Onboarding/Exit)
//      that REPLACES the old period-only "Repeats" select — for a period
//      value it drives `freq` exactly as before (recurrence UI unchanged);
//      for Onboarding/Exit it hides recurrence and reveals a booking-type
//      select instead. `freq`/`atTime` stay wire-required (49-03's
//      backward-compat decision, server nulls them for event rows — see
//      task-templates.ts) so we always keep them populated with an inert
//      default while hidden.
//   2. A per-item Role/Person toggle — assignedRole (existing Select)
//      stays the required fallback; a Switch optionally reveals a
//      Combobox over useListUsers() that pins assignedUserId. Identical
//      sub-form for EVERY trigger's items (period + event) — one editor,
//      no branching, per 49-CONTEXT's "UNIFY" directive.

const FREQS = ["daily", "weekly", "monthly"] as const;
const TRIGGERS = ["daily", "weekly", "monthly", "onboarding", "exit"] as const;
type Trigger = (typeof TRIGGERS)[number];
const TRIGGER_LABELS: Record<Trigger, string> = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  onboarding: "Onboarding",
  exit: "Exit",
};
function isEventTrigger(trigger: Trigger): trigger is "onboarding" | "exit" {
  return trigger === "onboarding" || trigger === "exit";
}

// Booking-type taxonomy per 49-CONTEXT/49-RESEARCH: the 5 auto-trigger types
// have real backing booking schema; team_cabin/visitor are manual-start-only
// (no backing schema yet — see task-templates.ts POST /start-procedure).
// No server enum CHECK on event_booking_type (Zod-validated only, taxonomy
// still settling) — this list is the authoring-time menu, not a hard schema.
const ONBOARDING_BOOKING_TYPES: { value: string; label: string; manualOnly?: boolean }[] = [
  { value: "day_pass", label: "Day Pass" },
  { value: "workstation", label: "Workstation" },
  { value: "private_cabin", label: "Private Cabin" },
  { value: "team_cabin", label: "Team Cabin", manualOnly: true },
  { value: "conference_new", label: "Conference — New user" },
  { value: "conference_existing", label: "Conference — Existing user" },
  { value: "visitor", label: "Visitor", manualOnly: true },
  { value: "meeting", label: "Meeting" },
  // Phase 42 (TBLD-04) — operator's non-recurring list maps Day Pass Intake
  // onto the EXISTING day_pass value above and Conference Room Booking onto
  // the EXISTING conference_new/conference_existing values above (never
  // duplicated). These two are genuinely new types with no backing booking
  // flow yet, so — like team_cabin/visitor — they stay manualOnly.
  { value: "week_pass", label: "Week Pass Trial", manualOnly: true },
  { value: "client_onboarding", label: "Client Onboarding Booking", manualOnly: true },
];
// ⚠ Widened to match ONBOARDING_BOOKING_TYPES's shape (manualOnly?: boolean)
// rather than dropping the flag — client_exit below needs it.
const EXIT_BOOKING_TYPES: { value: string; label: string; manualOnly?: boolean }[] = [
  { value: "workstation", label: "Workstation" },
  { value: "private_cabin", label: "Private Cabin" },
  // Phase 42 (TBLD-04) — new, manual-start-only (no backing booking flow yet).
  { value: "client_exit", label: "Client Exit", manualOnly: true },
];
function bookingTypeOptionsFor(trigger: Trigger): { value: string; label: string; manualOnly?: boolean }[] {
  if (trigger === "onboarding") return ONBOARDING_BOOKING_TYPES;
  if (trigger === "exit") return EXIT_BOOKING_TYPES;
  return [];
}
function bookingTypeLabel(bookingType: string | null | undefined): string {
  if (!bookingType) return "—";
  return bookingType
    .split("_")
    .map((w) => (w.length > 0 ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(" ");
}
const PRIORITIES: TaskPriority[] = ["P1", "P2", "P3", "P4"];
// Lockstep with lib/db/src/schema/users.ts's USER_ROLES const (repo
// convention: each Settings page declares its own copy rather than a shared
// import — see routing-settings.tsx / escalation-settings.tsx).
const ALL_ROLES = ["super_admin", "admin", "centralised_team", "agent", "ops_lead", "front_desk", "housekeeping", "bde"] as const;

// Phase 42 (TBLD-02) — proof-to-validate taxonomy for the item modal; mirrors
// the generated ChecklistProofType enum (lib/api-spec/openapi.yaml) as a
// local copy, same repo convention as ALL_ROLES above (each Settings page
// declares its own copy rather than importing the generated const object).
const PROOF_TYPES = ["none", "photo", "note", "signature"] as const;
type ProofType = (typeof PROOF_TYPES)[number];
const PROOF_TYPE_LABELS: Record<ProofType, string> = {
  none: "None",
  photo: "Photo",
  note: "Note",
  signature: "Signature",
};

const WEEKDAYS: { value: string; label: string }[] = [
  { value: "MO", label: "Monday" },
  { value: "TU", label: "Tuesday" },
  { value: "WE", label: "Wednesday" },
  { value: "TH", label: "Thursday" },
  { value: "FR", label: "Friday" },
  { value: "SA", label: "Saturday" },
  { value: "SU", label: "Sunday" },
];
const WEEKDAY_LABELS: Record<string, string> = Object.fromEntries(WEEKDAYS.map((w) => [w.value, w.label]));
const NTH_OPTIONS = [1, 2, 3, 4] as const;

function weekdayLabel(code: string | null | undefined): string {
  if (!code) return "";
  return WEEKDAY_LABELS[code] ?? code;
}
function ordinal(n: number): string {
  const rem10 = n % 10;
  const rem100 = n % 100;
  if (rem10 === 1 && rem100 !== 11) return `${n}st`;
  if (rem10 === 2 && rem100 !== 12) return `${n}nd`;
  if (rem10 === 3 && rem100 !== 13) return `${n}rd`;
  return `${n}th`;
}
function freqSummary(t: TaskTemplateDetail): string {
  if (t.freq === "daily") return `Daily · ${t.atTime}`;
  if (t.freq === "weekly") return `Weekly · ${weekdayLabel(t.byweekday) || "—"} · ${t.atTime}`;
  if (t.byweekday && t.nth) return `Monthly · ${ordinal(t.nth)} ${weekdayLabel(t.byweekday)} · ${t.atTime}`;
  if (t.byMonthDay) return `Monthly · Day ${t.byMonthDay} · ${t.atTime}`;
  return `Monthly · ${t.atTime}`;
}
// Phase 49 Plan 09 — `trigger` is the primary discriminator (falls back to
// `freq` only defensively; the DB backfilled trigger=freq for every
// pre-existing period row at migration 0113, so this should always be
// populated in practice — see task-templates.ts).
function scheduleSummary(t: TaskTemplateDetail): string {
  const trigger = (t.trigger ?? t.freq) as Trigger;
  if (isEventTrigger(trigger)) {
    return `${TRIGGER_LABELS[trigger]} · ${bookingTypeLabel(t.eventBookingType)}`;
  }
  return freqSummary(t);
}

function errStatus(err: unknown): number | undefined {
  return (err as { status?: number })?.status;
}
function errData(err: unknown): Record<string, unknown> | undefined {
  return (err as { data?: Record<string, unknown> })?.data;
}

const RecurrenceShape = {
  freq: z.enum(FREQS),
  byweekday: z.string().nullable().optional(),
  nth: z.number().nullable().optional(),
  byMonthDay: z.number().nullable().optional(),
  atTime: z.string().min(1, "Reset time is required"),
};

// Base shapes stay plain ZodObjects (not ZodEffects) so EditObjectSchema can
// still `.extend()` CreateObjectSchema — the cross-field superRefine below is
// applied AFTER extending, mirroring the server's own
// BaseCreateTaskTemplateBody.superRefine(...) wrapping convention
// (task-templates.ts) rather than hand-editing a generated schema.
const CreateObjectSchema = z.object({
  name: z.string().min(1, "Name is required"),
  priority: z.enum(["P1", "P2", "P3", "P4"]),
  trigger: z.enum(TRIGGERS),
  // Set only for onboarding/exit triggers — enforced below via superRefine,
  // not a bare .min(1) here (period templates must be ALLOWED to leave it null).
  eventBookingType: z.string().nullable().optional(),
  ...RecurrenceShape,
});
const EditObjectSchema = CreateObjectSchema.extend({ active: z.boolean() });

function requireEventBookingType(
  data: { trigger: Trigger; eventBookingType?: string | null },
  ctx: z.RefinementCtx,
) {
  if (isEventTrigger(data.trigger) && !data.eventBookingType) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["eventBookingType"],
      message: "Booking type is required for Onboarding/Exit checklists",
    });
  }
}

const CreateSchema = CreateObjectSchema.superRefine(requireEventBookingType);
const EditSchema = EditObjectSchema.superRefine(requireEventBookingType);
type CreateValues = z.infer<typeof CreateSchema>;
type EditValues = z.infer<typeof EditSchema>;

const EMPTY_CREATE: CreateValues = {
  name: "",
  priority: "P4",
  trigger: "daily",
  eventBookingType: null,
  freq: "daily",
  byweekday: null,
  nth: null,
  byMonthDay: null,
  atTime: "08:00",
};

const ItemSchema = z.object({
  label: z.string().min(1, "Label is required"),
  required: z.boolean(),
  assignedRole: z.enum(ALL_ROLES),
  // Phase 49 (TASN-01/02) — nullable person-pin override; assignedRole
  // stays required as the fallback ("role default, optional person
  // override" — 49-CONTEXT). Presence-when-pinned is enforced in
  // onItemSubmit (tied to the UI-only itemAssignMode toggle, not a schema
  // field), matching this file's existing convention of keeping bare
  // cross-field UI state (e.g. active byweekday/byMonthDay exclusivity)
  // out of the Zod shape itself.
  assignedUserId: z.number().nullable().optional(),
  zoneLabel: z.string().min(1, "Zone is required"),
  zoneResourceId: z.number().nullable().optional(),
  // Group-checklist UI (migration 0120) — free-text collapsible-section
  // label (e.g. "Dusting"); real ItemSchema field (not a local-state UI
  // toggle like itemAssignMode/validatorMode below), so it renders as a
  // normal <FormField> further down. Null/blank keeps the item ungrouped.
  groupLabel: z.string().nullable().optional(),
  // Phase 42 (TBLD-01/02) — full task spec. `atTimes` deliberately stays OUT
  // of this Zod shape (like assignedUserId's UI-only pairing above): it's
  // driven by the occurrenceTimes LOCAL STATE stepper below, not a
  // react-hook-form field, and is validated/assembled in onItemSubmit.
  // `timesPerformed` is NEVER a field anywhere in this schema — it is
  // derived server-side as atTimes.length (readOnly response field).
  completeWithinMinutes: z.number().int().min(0, "Must be 0 or more").nullable().optional(),
  proofType: z.enum(PROOF_TYPES).nullable().optional(),
  // Explicit validator role OR person; blank on BOTH (validatorMode local
  // state === "blank") falls back to the doer's manager_id server-side
  // (Phase 44's auto rule). Presence-when-picked is enforced in
  // onItemSubmit, mirroring assignedUserId/itemAssignMode above.
  validatorRole: z.string().nullable().optional(),
  validatorUserId: z.number().nullable().optional(),
});
type ItemValues = z.infer<typeof ItemSchema>;
const EMPTY_ITEM: ItemValues = {
  label: "",
  required: true,
  assignedRole: "housekeeping",
  assignedUserId: null,
  zoneLabel: "",
  zoneResourceId: null,
  groupLabel: null,
  completeWithinMinutes: null,
  proofType: "none",
  validatorRole: null,
  validatorUserId: null,
};

// Phase 42 (TBLD-01) — one-line item-list spec summary (Claude's Discretion
// per 42-CONTEXT): times · window · validator as plain text; proof type is
// rendered as a separate Badge by the caller. A legacy item with no spec at
// all (every field below unset) returns "" so the caller renders nothing —
// no "—" noise on pre-Phase-42 seeded templates.
function itemValidatorLabel(it: TaskTemplateItem, users: User[], usersUnavailable: boolean): string | null {
  if (it.validatorUserId != null) {
    return usersUnavailable ? `User #${it.validatorUserId}` : (users.find((u) => u.id === it.validatorUserId)?.name ?? `User #${it.validatorUserId}`);
  }
  if (it.validatorRole) return it.validatorRole.replace(/_/g, " ");
  return null;
}
function itemSpecSummaryText(it: TaskTemplateItem, users: User[], usersUnavailable: boolean): string {
  const parts: string[] = [];
  if (it.atTimes && it.atTimes.length > 0) parts.push(it.atTimes.join(", "));
  if (it.completeWithinMinutes != null) parts.push(`${it.completeWithinMinutes}m`);
  const validator = itemValidatorLabel(it, users, usersUnavailable);
  if (validator) parts.push(`Validator: ${validator}`);
  return parts.join(" · ");
}

// Phase 49 Plan 09 (TASN-01/02) — the per-item person-pin picker. Mirrors
// operations/contact-combobox.tsx's Popover+Command shape (this repo's
// established combobox convention), adapted to a small in-memory roster
// (useListUsers()) rather than a paginated hook — no search-as-you-fetch
// needed for a teammate list.
function AssignedUserCombobox({ value, onChange, users, disabled }: {
  value: number | null;
  onChange: (id: number | null) => void;
  users: User[];
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const selected = users.find((u) => u.id === value) ?? null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          {selected ? (
            <span className="flex items-center gap-2 truncate">
              <span className="font-medium truncate">{selected.name}</span>
              <span className="text-xs text-muted-foreground capitalize shrink-0">{selected.role.replace(/_/g, " ")}</span>
            </span>
          ) : (
            <span className="text-muted-foreground">Search teammates…</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search by name or role…" aria-label="Search teammates" />
          <CommandList>
            <CommandEmpty>No teammate matches.</CommandEmpty>
            <CommandGroup>
              {users.map((u) => (
                <CommandItem
                  key={u.id}
                  value={`${u.name} ${u.role}`}
                  onSelect={() => { onChange(u.id); setOpen(false); }}
                >
                  <div className="flex w-full items-center justify-between gap-2">
                    <span className="text-sm truncate">{u.name}</span>
                    <span className="text-xs text-muted-foreground capitalize shrink-0">{u.role.replace(/_/g, " ")}</span>
                  </div>
                  {selected?.id === u.id ? <Check className="ml-2 h-4 w-4 shrink-0" /> : null}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function TaskChecklistSettingsPage() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "super_admin" || user?.role === "ops_lead";

  const queryClient = useQueryClient();
  const { data: templatesData, isLoading } = useListTaskTemplates();
  const { data: resources } = useListSpaceResources();
  // Phase 49 Plan 09 (TASN-01/02) — the per-item person-pin picker. Mirrors
  // checklist-daily-tab.tsx's own usersUnavailable graceful degradation
  // (retry:false so a 403 settles fast); this Settings page is already
  // canEditConfig-gated to admin/ops_lead so useListUsers should never 403
  // here in practice, but we degrade to role-only anyway for defense.
  const { data: users, isError: usersUnavailable } = useListUsers({
    query: { retry: false, queryKey: getListUsersQueryKey() },
  });

  const [openCreate, setOpenCreate] = useState(false);
  const [editTargetId, setEditTargetId] = useState<number | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<TaskTemplateDetail | null>(null);
  // Hotfix (permanent Delete) — a REAL delete, distinct from archiveTarget's
  // soft archive above. Opens its own AlertDialog (reuses the delete-item
  // confirm's plain-markup pattern below) showing the honest item/round
  // counts before committing.
  const [permanentDeleteTarget, setPermanentDeleteTarget] = useState<TaskTemplateDetail | null>(null);
  // Hotfix follow-up (2026-07-18, "force resync") — a 409 round_has_progress opens this
  // confirm instead of just toasting; confirming re-calls onSyncToday(true) (force). No
  // target payload needed — always scoped to the currently-open editTarget.
  const [syncForceConfirmOpen, setSyncForceConfirmOpen] = useState(false);

  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<TaskTemplateItem | null>(null);
  const [deleteItemTarget, setDeleteItemTarget] = useState<TaskTemplateItem | null>(null);
  const [reorderingItemId, setReorderingItemId] = useState<number | null>(null);
  // Role/Person toggle — UI-only (not sent to the API); assignedUserId
  // itself is the wire signal. "person" with no id yet picked is caught in
  // onItemSubmit before submit (keeps the toggle and the stored value in sync).
  const [itemAssignMode, setItemAssignMode] = useState<"role" | "person">("role");
  // Phase 42 (TBLD-01/02) — validator tri-state, UI-only like itemAssignMode
  // above but with a real third state: "blank" IS a valid, meaningful choice
  // (falls back to the doer's manager) — never pre-selected on purpose (would
  // freeze today's org chart into the template, per 42-CONTEXT).
  const [validatorMode, setValidatorMode] = useState<"blank" | "role" | "person">("blank");
  // Phase 42 (TBLD-01) — the occurrence-times list. LOCAL STATE, not a
  // react-hook-form field: "times performed" is a UI stepper only —
  // timesPerformed is DERIVED server-side as atTimes.length and is never
  // itself submitted. Assembled into the wire payload's atTimes[] in
  // onItemSubmit.
  const [occurrenceTimes, setOccurrenceTimes] = useState<string[]>([]);

  const createMutation = useCreateTaskTemplate();
  const updateMutation = useUpdateTaskTemplate();
  const archiveMutation = useDeleteTaskTemplate();
  const permanentDeleteMutation = usePermanentlyDeleteTaskTemplate();
  const createItemMutation = useCreateTaskTemplateItem();
  const updateItemMutation = useUpdateTaskTemplateItem();
  const deleteItemMutation = useDeleteTaskTemplateItem();
  const reorderItemMutation = useReorderTaskTemplateItem();
  // Hotfix (2026-07-18, "Sync today's round") — TREC-07's frozen-copy-at-spawn
  // design means today's already-spawned round never reflects a later item
  // edit; this lets the operator force it on demand instead of waiting for
  // tomorrow. See onSyncToday() below.
  const syncTodayMutation = useSyncTaskTemplateToday();

  const createForm = useForm<CreateValues>({ resolver: zodResolver(CreateSchema), defaultValues: EMPTY_CREATE });
  const editForm = useForm<EditValues>({ resolver: zodResolver(EditSchema), defaultValues: { ...EMPTY_CREATE, active: true } });
  const itemForm = useForm<ItemValues>({ resolver: zodResolver(ItemSchema), defaultValues: EMPTY_ITEM });

  const templates = templatesData ?? [];
  // Re-derived from the list on every render (never a stale local copy) so
  // that an item add/edit/delete/reorder — which invalidates the list query
  // — is immediately reflected inside the still-open Edit dialog. Avoids a
  // second N+1 useGetTaskTemplate fetch per template (43-05 already nests
  // items on the list response for exactly this reason).
  const editTarget = templates.find((t) => t.id === editTargetId) ?? null;
  // `trigger` (not `freq`) now drives every conditional in both forms — the
  // Trigger select's onValueChange keeps `freq` mirrored for period values
  // (see handleCreateTriggerChange/handleEditTriggerChange below).
  const createTrigger = createForm.watch("trigger");
  const editTrigger = editForm.watch("trigger");

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: getListTaskTemplatesQueryKey() });
  }
  // Handles both Create + Edit Trigger selects — resets the
  // recurrence/booking-type fields that don't apply to the newly-picked
  // trigger, and mirrors `freq` for period values so the wire-required freq
  // field (49-03 backward-compat) always stays in sync with what the user
  // sees. For event triggers, freq/atTime become inert filler (the route
  // nulls them at persistence time — task-templates.ts) so any valid
  // default is fine. Kept as two thin functions (not one generic helper)
  // to sidestep react-hook-form's UseFormReturn<T> generic-method variance.
  function handleCreateTriggerChange(next: Trigger) {
    createForm.setValue("trigger", next);
    createForm.setValue("eventBookingType", null);
    createForm.setValue("byweekday", null);
    createForm.setValue("nth", null);
    createForm.setValue("byMonthDay", null);
    if (next === "daily" || next === "weekly" || next === "monthly") {
      createForm.setValue("freq", next);
    } else {
      createForm.setValue("freq", "daily");
      if (!createForm.getValues("atTime")) createForm.setValue("atTime", "08:00");
    }
  }
  function handleEditTriggerChange(next: Trigger) {
    editForm.setValue("trigger", next);
    editForm.setValue("eventBookingType", null);
    editForm.setValue("byweekday", null);
    editForm.setValue("nth", null);
    editForm.setValue("byMonthDay", null);
    if (next === "daily" || next === "weekly" || next === "monthly") {
      editForm.setValue("freq", next);
    } else {
      editForm.setValue("freq", "daily");
      if (!editForm.getValues("atTime")) editForm.setValue("atTime", "08:00");
    }
  }
  function openEdit(t: TaskTemplateDetail) {
    setEditTargetId(t.id);
    editForm.reset({
      name: t.name,
      priority: t.priority,
      trigger: (t.trigger ?? t.freq) as Trigger,
      eventBookingType: t.eventBookingType ?? null,
      freq: t.freq,
      byweekday: t.byweekday ?? null,
      nth: t.nth ?? null,
      byMonthDay: t.byMonthDay ?? null,
      atTime: t.atTime,
      active: t.active,
    });
  }
  function closeDialogs() {
    setOpenCreate(false);
    setEditTargetId(null);
    createForm.reset(EMPTY_CREATE);
  }
  function openAddItem() {
    if (!editTarget) return;
    setEditingItem(null);
    setItemAssignMode("role");
    setValidatorMode("blank");
    setOccurrenceTimes([]);
    itemForm.reset(EMPTY_ITEM);
    setItemDialogOpen(true);
  }
  function openEditItem(it: TaskTemplateItem) {
    setEditingItem(it);
    setItemAssignMode(it.assignedUserId != null ? "person" : "role");
    setValidatorMode(it.validatorUserId != null ? "person" : it.validatorRole ? "role" : "blank");
    setOccurrenceTimes(it.atTimes && it.atTimes.length > 0 ? [...it.atTimes] : []);
    itemForm.reset({
      label: it.label,
      required: it.required,
      assignedRole: it.assignedRole,
      assignedUserId: it.assignedUserId ?? null,
      zoneLabel: it.zoneLabel,
      zoneResourceId: it.zoneResourceId ?? null,
      groupLabel: it.groupLabel ?? null,
      completeWithinMinutes: it.completeWithinMinutes ?? null,
      proofType: (it.proofType as ProofType | null | undefined) ?? "none",
      validatorRole: it.validatorRole ?? null,
      validatorUserId: it.validatorUserId ?? null,
    });
    setItemDialogOpen(true);
  }
  function closeItemDialog() {
    setItemDialogOpen(false);
    setEditingItem(null);
    setItemAssignMode("role");
    setValidatorMode("blank");
    setOccurrenceTimes([]);
    itemForm.reset(EMPTY_ITEM);
  }

  async function onCreate(values: CreateValues) {
    try {
      const created = await createMutation.mutateAsync({ data: values });
      toast.success(`Created "${values.name}". Add items below.`);
      invalidate();
      setOpenCreate(false);
      createForm.reset(EMPTY_CREATE);
      // POST /task-templates never accepts items inline (43-05) — jump
      // straight into Edit so the items table is right there.
      setEditTargetId(created.id);
    } catch (err) {
      const status = errStatus(err);
      toast.error(
        status === 403
          ? ((errData(err)?.message as string) ?? "You don't have permission to add checklists.")
          : "Could not create the checklist.",
      );
    }
  }
  async function onEdit(values: EditValues) {
    if (!editTarget) return;
    try {
      await updateMutation.mutateAsync({ id: editTarget.id, data: values });
      toast.success("Checklist saved.");
      invalidate();
    } catch (err) {
      const status = errStatus(err);
      toast.error(
        status === 403
          ? ((errData(err)?.message as string) ?? "You don't have permission to edit checklists.")
          : "Could not save the checklist.",
      );
    }
  }
  async function onArchive() {
    if (!archiveTarget) return;
    try {
      await archiveMutation.mutateAsync({ id: archiveTarget.id });
      toast.success(`Archived "${archiveTarget.name}". Today's already-spawned round is unaffected.`);
      invalidate();
      setArchiveTarget(null);
    } catch (err) {
      const status = errStatus(err);
      toast.error(
        status === 403
          ? ((errData(err)?.message as string) ?? "You don't have permission to archive checklists.")
          : "Could not archive the checklist.",
      );
    }
  }
  // Hotfix (permanent Delete) — a REAL delete, not the archive above. The
  // AlertDialog already showed the honest pre-delete counts (items.length +
  // spawnedRoundCount, both already loaded on this page); the success toast
  // uses the RESPONSE's counts instead (the authoritative as-of-delete
  // numbers, captured server-side inside the same transaction).
  async function onPermanentDelete() {
    if (!permanentDeleteTarget) return;
    try {
      const result = await permanentDeleteMutation.mutateAsync({ id: permanentDeleteTarget.id });
      toast.success(
        `Deleted "${permanentDeleteTarget.name}". ${result.deletedItems} item(s) removed; ${result.unlinkedRounds} past round(s) kept, unlinked.`,
      );
      invalidate();
      queryClient.invalidateQueries({ queryKey: getGetDailyChecklistQueryKey() });
      setPermanentDeleteTarget(null);
      // The Edit sheet may be open on the very template just deleted (its
      // Delete action lives outside the sheet, in the table row, but nothing
      // stops both being triggered in sequence) — close it rather than leave
      // it pointed at a now-nonexistent id.
      if (editTargetId === permanentDeleteTarget.id) closeDialogs();
    } catch (err) {
      const status = errStatus(err);
      toast.error(
        status === 403
          ? ((errData(err)?.message as string) ?? "You don't have permission to delete checklists.")
          : status === 404
            ? "This checklist was already removed."
            : "Could not delete the checklist.",
      );
    }
  }
  async function onRestore(t: TaskTemplateDetail) {
    try {
      await updateMutation.mutateAsync({ id: t.id, data: { active: true } });
      toast.success(`Restored "${t.name}".`);
      invalidate();
    } catch {
      toast.error("Could not restore the checklist.");
    }
  }
  // Hotfix (2026-07-18, "Sync today's round") — spawns today's round if none
  // exists yet, or regenerates its items from the CURRENT items if the round
  // exists with zero doer/validator progress. A 409 (real progress already
  // recorded) is a normal, expected outcome here — surfaced as a toast, never
  // a crash — not an error to auto-fix around.
  //
  // Hotfix follow-up (2026-07-18, "force resync") — `force` defaults false
  // (the exact prior call shape/behavior). A 409 now opens the confirm
  // AlertDialog below instead of just toasting; confirming it re-calls this
  // same function with force=true, which the server regenerates onto instead
  // of 409'ing (DISCARDING today's ticks/validations — irreversible, hence
  // the confirm step rather than a silent auto-retry).
  async function onSyncToday(force = false) {
    if (!editTarget) return;
    try {
      const result = await syncTodayMutation.mutateAsync({ id: editTarget.id, data: { force } });
      queryClient.invalidateQueries({ queryKey: getGetDailyChecklistQueryKey() });
      toast.success(
        result.action === "spawned"
          ? "Today's round has been created from the current items."
          : force
            ? "Today's round was regenerated — earlier progress was discarded."
            : "Today's round now matches the current items.",
      );
    } catch (err) {
      const status = errStatus(err);
      const data = errData(err);
      if (status === 409 && !force) {
        // Expected outcome, not an error to toast — the confirm dialog explains it and
        // offers the force-resync escape hatch.
        setSyncForceConfirmOpen(true);
      } else if (status === 409) {
        // Defensive only — force:true should never come back 409 from the server.
        toast.error((data?.message as string) ?? "Today's round has progress — new items apply from tomorrow.");
      } else if (status === 422) {
        toast.error((data?.message as string) ?? "Could not sync today's round.");
      } else if (status === 403) {
        toast.error((data?.message as string) ?? "You don't have permission to sync checklists.");
      } else {
        toast.error("Could not sync today's round.");
      }
    }
  }
  // Confirm handler for the force-resync AlertDialog — closes the dialog first (Radix's
  // own onOpenChange isn't reliable to chain after an async click handler resolves) then
  // fires the SAME onSyncToday with force=true.
  async function onForceSyncToday() {
    setSyncForceConfirmOpen(false);
    await onSyncToday(true);
  }
  async function onItemSubmit(values: ItemValues) {
    if (!editTarget) return;
    // Role/Person toggle is UI-only state, not part of ItemSchema — normalize
    // here so the wire payload always matches what the toggle shows: "role"
    // mode NEVER sends a stale assignedUserId, "person" mode REQUIRES one to
    // actually be picked (a bare toggle-flip with no selection is caught
    // before submit, rather than silently falling back to role).
    if (itemAssignMode === "person" && values.assignedUserId == null) {
      itemForm.setError("assignedUserId", { message: "Pick a teammate, or turn the person pin off." });
      return;
    }
    // Phase 42 (TBLD-01/02) — validatorMode is UI-only tri-state, not part of
    // ItemSchema. "blank" clears BOTH validator fields (⇒ manager fallback);
    // "role"/"person" each require their own field to actually be picked.
    if (validatorMode === "role" && !values.validatorRole) {
      itemForm.setError("validatorRole", { message: "Pick a validator role, or switch back to \"Doer's manager\"." });
      return;
    }
    if (validatorMode === "person" && values.validatorUserId == null) {
      itemForm.setError("validatorUserId", { message: "Pick a validator, or switch back to \"Doer's manager\"." });
      return;
    }
    // Occurrence times: LOCAL STATE, not a schema field (see occurrenceTimes
    // above) — client-validate HH:MM format here, per this file's convention
    // of keeping cross-field/local-toggle rules out of the Zod shape.
    const timeFormat = /^([01]\d|2[0-3]):([0-5]\d)$/;
    for (const t of occurrenceTimes) {
      if (!timeFormat.test(t)) {
        toast.error("Every occurrence time must be a valid HH:MM (24h).");
        return;
      }
    }
    const payload: ItemValues & { atTimes?: string[] | null } = {
      ...values,
      assignedUserId: itemAssignMode === "person" ? values.assignedUserId : null,
      validatorRole: validatorMode === "role" ? values.validatorRole : null,
      validatorUserId: validatorMode === "person" ? values.validatorUserId : null,
      // "Times performed" is never itself sent — only the explicit times.
      atTimes: occurrenceTimes.length > 0 ? occurrenceTimes : null,
    };
    try {
      if (editingItem) {
        await updateItemMutation.mutateAsync({ id: editTarget.id, itemId: editingItem.id, data: payload });
        toast.success("Item saved.");
      } else {
        await createItemMutation.mutateAsync({ id: editTarget.id, data: payload });
        toast.success("Item added.");
      }
      invalidate();
      closeItemDialog();
    } catch (err) {
      const status = errStatus(err);
      const data = errData(err);
      if (status === 403) toast.error((data?.message as string) ?? "You don't have permission to edit checklist items.");
      else if (status === 422) toast.error((data?.message as string) ?? "Check the item fields and try again.");
      else toast.error("Could not save the item.");
    }
  }
  async function onDeleteItem() {
    if (!editTarget || !deleteItemTarget) return;
    try {
      await deleteItemMutation.mutateAsync({ id: editTarget.id, itemId: deleteItemTarget.id });
      toast.success(`Removed "${deleteItemTarget.label}".`);
      invalidate();
      setDeleteItemTarget(null);
    } catch {
      toast.error("Could not remove the item.");
    }
  }
  async function moveItem(itemId: number, direction: "up" | "down") {
    if (!editTarget) return;
    setReorderingItemId(itemId);
    try {
      await reorderItemMutation.mutateAsync({ id: editTarget.id, itemId, data: { direction } });
      invalidate();
    } catch (err) {
      const status = errStatus(err);
      const data = errData(err);
      if (status === 422 && data?.error === "no_neighbor") {
        toast.error(direction === "up" ? "Already at the top." : "Already at the bottom.");
      } else if (status === 403) {
        toast.error((data?.message as string) ?? "You don't have permission to reorder items.");
      } else {
        toast.error("Could not reorder the item.");
      }
    } finally {
      setReorderingItemId(null);
    }
  }

  if (!canEdit) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5" /> Restricted</CardTitle>
          <CardDescription>Checklist settings are limited to admin and ops lead roles.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const sorted = templates;
  const sortedItems = editTarget ? [...editTarget.items].sort((a, b) => a.seq - b.seq) : [];
  // Group-checklist UI (migration 0120) — distinct existing group labels on
  // THIS template, offered as native <datalist> suggestions on the Group
  // input below (free text, not a closed enum — the column carries no CHECK).
  const existingGroupLabels = Array.from(
    new Set((editTarget?.items ?? []).map((it) => it.groupLabel).filter((g): g is string => !!g)),
  ).sort();

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <ListChecks className="h-6 w-6 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Checklists</h1>
            <p className="text-sm text-muted-foreground">
              Recurring rounds that spawn fresh each period. Edits affect future rounds only — today's already-spawned
              checklist never changes.
            </p>
          </div>
        </div>
        <Button onClick={() => setOpenCreate(true)}>Add Checklist</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Templates {sorted.length ? <span className="text-muted-foreground font-normal text-sm">({sorted.length})</span> : null}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Trigger</TableHead>
                <TableHead>Schedule</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right w-[220px] pr-4">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(3)].map((_, i) => (
                  <TableRow key={i}>{[...Array(7)].map((__, j) => <TableCell key={j}><Skeleton className="h-4 w-16" /></TableCell>)}</TableRow>
                ))
              ) : sorted.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="h-24 text-center text-muted-foreground">No checklists yet.</TableCell></TableRow>
              ) : (
                sorted.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-medium text-sm">{t.name}</TableCell>
                    <TableCell>
                      <Badge variant={isEventTrigger((t.trigger ?? t.freq) as Trigger) ? "outline" : "secondary"}>
                        {TRIGGER_LABELS[(t.trigger ?? t.freq) as Trigger]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{scheduleSummary(t)}</TableCell>
                    <TableCell><Badge variant="secondary">{t.priority}</Badge></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{t.items.length}</TableCell>
                    <TableCell><Badge variant={t.active ? "default" : "outline"}>{t.active ? "Active" : "Archived"}</Badge></TableCell>
                    <TableCell className="text-right whitespace-nowrap pr-4">
                      <div className="flex justify-end items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(t)}>Edit</Button>
                        {t.active ? (
                          <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => setArchiveTarget(t)}>
                            Archive
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost" onClick={() => onRestore(t)}>Restore</Button>
                        )}
                        {/* Hotfix (permanent Delete) — a REAL delete, always available
                            regardless of active/archived state; distinct from Archive
                            above (the non-destructive default). */}
                        <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => setPermanentDeleteTarget(t)}>
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create dialog — template fields only (no items yet; POST
          /task-templates/{id}/items happens after creation, per 43-05). */}
      <Sheet open={openCreate} onOpenChange={(o) => !o && closeDialogs()}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Add Checklist</SheetTitle>
            <SheetDescription>Set the name and reset schedule now — add items after creating.</SheetDescription>
          </SheetHeader>
          <Form {...createForm}>
            <form onSubmit={createForm.handleSubmit(onCreate)} className="space-y-3">
              <FormField name="name" control={createForm.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Name <span className="text-destructive">*</span></FormLabel>
                  <FormControl><Input placeholder="e.g. Daily checklist" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="priority" control={createForm.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Priority</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl><SelectTrigger className="max-w-[160px]"><SelectValue /></SelectTrigger></FormControl>
                    <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="trigger" control={createForm.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Trigger</FormLabel>
                  <Select value={field.value} onValueChange={(v) => handleCreateTriggerChange(v as Trigger)}>
                    <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                    <SelectContent>
                      {TRIGGERS.map((t) => <SelectItem key={t} value={t}>{TRIGGER_LABELS[t]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    {isEventTrigger(createTrigger)
                      ? "Spawns automatically when the matching booking/membership event fires (or via manual Start-procedure for Team Cabin/Visitor)."
                      : "A self-spawning round on this schedule — set the reset time below."}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )} />
              {isEventTrigger(createTrigger) && (
                <FormField name="eventBookingType" control={createForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Booking type <span className="text-destructive">*</span></FormLabel>
                    <Select value={field.value ?? ""} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger><SelectValue placeholder="Pick a booking type" /></SelectTrigger></FormControl>
                      <SelectContent>
                        {bookingTypeOptionsFor(createTrigger).map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}{o.manualOnly ? " (manual start only)" : ""}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>Team Cabin and Visitor have no auto-trigger yet — they spawn only via a staff "Start procedure" action.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )} />
              )}
              {!isEventTrigger(createTrigger) && (
                <>
                  {createTrigger === "weekly" && (
                    <FormField name="byweekday" control={createForm.control} render={({ field }) => (
                      <FormItem>
                        <FormLabel>Day of week <span className="text-destructive">*</span></FormLabel>
                        <Select value={field.value ?? ""} onValueChange={field.onChange}>
                          <FormControl><SelectTrigger><SelectValue placeholder="Pick a day" /></SelectTrigger></FormControl>
                          <SelectContent>{WEEKDAYS.map((w) => <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}</SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )} />
                  )}
                  {createTrigger === "monthly" && (
                    <div className="space-y-3 rounded-md border p-3">
                      <p className="text-xs text-muted-foreground">Set EITHER a day of month OR a weekday position — not both.</p>
                      <FormField name="byMonthDay" control={createForm.control} render={({ field }) => (
                        <FormItem>
                          <FormLabel>Day of month</FormLabel>
                          <FormControl>
                            <Input
                              type="number" min={1} max={31} placeholder="e.g. 1"
                              value={field.value ?? ""}
                              onChange={(e) => {
                                const v = e.target.value === "" ? null : Number(e.target.value);
                                field.onChange(v);
                                if (v !== null) { createForm.setValue("byweekday", null); createForm.setValue("nth", null); }
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                      <div className="grid grid-cols-2 gap-2">
                        <FormField name="nth" control={createForm.control} render={({ field }) => (
                          <FormItem>
                            <FormLabel>Position</FormLabel>
                            <Select
                              value={field.value != null ? String(field.value) : ""}
                              onValueChange={(v) => { field.onChange(Number(v)); createForm.setValue("byMonthDay", null); }}
                            >
                              <FormControl><SelectTrigger><SelectValue placeholder="1st…4th" /></SelectTrigger></FormControl>
                              <SelectContent>{NTH_OPTIONS.map((n) => <SelectItem key={n} value={String(n)}>{ordinal(n)}</SelectItem>)}</SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )} />
                        <FormField name="byweekday" control={createForm.control} render={({ field }) => (
                          <FormItem>
                            <FormLabel>Weekday</FormLabel>
                            <Select
                              value={field.value ?? ""}
                              onValueChange={(v) => { field.onChange(v); createForm.setValue("byMonthDay", null); }}
                            >
                              <FormControl><SelectTrigger><SelectValue placeholder="Pick a day" /></SelectTrigger></FormControl>
                              <SelectContent>{WEEKDAYS.map((w) => <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}</SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )} />
                      </div>
                    </div>
                  )}
                  <FormField name="atTime" control={createForm.control} render={({ field }) => (
                    <FormItem>
                      <FormLabel>Reset time (IST) <span className="text-destructive">*</span></FormLabel>
                      <FormControl><Input type="time" {...field} /></FormControl>
                      <FormDescription>The fresh round spawns at this time each period, all items unchecked.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )} />
                </>
              )}
              <SheetFooter className="mt-4">
                <Button type="button" variant="outline" onClick={closeDialogs}>Cancel</Button>
                <Button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? "Creating…" : "Create & Add Items"}</Button>
              </SheetFooter>
            </form>
          </Form>
        </SheetContent>
      </Sheet>

      {/* Edit dialog — template fields + the nested items table. */}
      <Sheet open={editTarget !== null} onOpenChange={(o) => !o && closeDialogs()}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Edit {editTarget?.name}</SheetTitle>
            <SheetDescription>Changes apply to future rounds only — today's already-spawned checklist is untouched.</SheetDescription>
          </SheetHeader>
          {editTarget && (
            <>
              <Form {...editForm}>
                <form onSubmit={editForm.handleSubmit(onEdit)} className="space-y-3">
                  <FormField name="name" control={editForm.control} render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name <span className="text-destructive">*</span></FormLabel>
                      <FormControl><Input {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <div className="grid grid-cols-2 gap-3">
                    <FormField name="priority" control={editForm.control} render={({ field }) => (
                      <FormItem>
                        <FormLabel>Priority</FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                          <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField name="trigger" control={editForm.control} render={({ field }) => (
                      <FormItem>
                        <FormLabel>Trigger</FormLabel>
                        <Select value={field.value} onValueChange={(v) => handleEditTriggerChange(v as Trigger)}>
                          <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                          <SelectContent>
                            {TRIGGERS.map((t) => <SelectItem key={t} value={t}>{TRIGGER_LABELS[t]}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                  {isEventTrigger(editTrigger) && (
                    <FormField name="eventBookingType" control={editForm.control} render={({ field }) => (
                      <FormItem>
                        <FormLabel>Booking type <span className="text-destructive">*</span></FormLabel>
                        <Select value={field.value ?? ""} onValueChange={field.onChange}>
                          <FormControl><SelectTrigger><SelectValue placeholder="Pick a booking type" /></SelectTrigger></FormControl>
                          <SelectContent>
                            {bookingTypeOptionsFor(editTrigger).map((o) => (
                              <SelectItem key={o.value} value={o.value}>{o.label}{o.manualOnly ? " (manual start only)" : ""}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>Team Cabin and Visitor have no auto-trigger yet — they spawn only via a staff "Start procedure" action.</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )} />
                  )}
                  {!isEventTrigger(editTrigger) && (
                    <>
                      {editTrigger === "weekly" && (
                        <FormField name="byweekday" control={editForm.control} render={({ field }) => (
                          <FormItem>
                            <FormLabel>Day of week <span className="text-destructive">*</span></FormLabel>
                            <Select value={field.value ?? ""} onValueChange={field.onChange}>
                              <FormControl><SelectTrigger><SelectValue placeholder="Pick a day" /></SelectTrigger></FormControl>
                              <SelectContent>{WEEKDAYS.map((w) => <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}</SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )} />
                      )}
                      {editTrigger === "monthly" && (
                        <div className="space-y-3 rounded-md border p-3">
                          <p className="text-xs text-muted-foreground">Set EITHER a day of month OR a weekday position — not both.</p>
                          <FormField name="byMonthDay" control={editForm.control} render={({ field }) => (
                            <FormItem>
                              <FormLabel>Day of month</FormLabel>
                              <FormControl>
                                <Input
                                  type="number" min={1} max={31} placeholder="e.g. 1"
                                  value={field.value ?? ""}
                                  onChange={(e) => {
                                    const v = e.target.value === "" ? null : Number(e.target.value);
                                    field.onChange(v);
                                    if (v !== null) { editForm.setValue("byweekday", null); editForm.setValue("nth", null); }
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                          <div className="grid grid-cols-2 gap-2">
                            <FormField name="nth" control={editForm.control} render={({ field }) => (
                              <FormItem>
                                <FormLabel>Position</FormLabel>
                                <Select
                                  value={field.value != null ? String(field.value) : ""}
                                  onValueChange={(v) => { field.onChange(Number(v)); editForm.setValue("byMonthDay", null); }}
                                >
                                  <FormControl><SelectTrigger><SelectValue placeholder="1st…4th" /></SelectTrigger></FormControl>
                                  <SelectContent>{NTH_OPTIONS.map((n) => <SelectItem key={n} value={String(n)}>{ordinal(n)}</SelectItem>)}</SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )} />
                            <FormField name="byweekday" control={editForm.control} render={({ field }) => (
                              <FormItem>
                                <FormLabel>Weekday</FormLabel>
                                <Select
                                  value={field.value ?? ""}
                                  onValueChange={(v) => { field.onChange(v); editForm.setValue("byMonthDay", null); }}
                                >
                                  <FormControl><SelectTrigger><SelectValue placeholder="Pick a day" /></SelectTrigger></FormControl>
                                  <SelectContent>{WEEKDAYS.map((w) => <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}</SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )} />
                          </div>
                        </div>
                      )}
                      <FormField name="atTime" control={editForm.control} render={({ field }) => (
                        <FormItem>
                          <FormLabel>Reset time (IST) <span className="text-destructive">*</span></FormLabel>
                          <FormControl><Input type="time" {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                    </>
                  )}
                  <FormField name="active" control={editForm.control} render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-md border p-3">
                      <div>
                        <FormLabel>Active</FormLabel>
                        <p className="text-xs text-muted-foreground">Off = archived; stops future spawns only (TREC-10).</p>
                      </div>
                      <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                    </FormItem>
                  )} />
                  <SheetFooter className="mt-2 sm:justify-between">
                    {/* Hotfix (2026-07-18, "Sync today's round") — plain markup, not a
                        FormField, so it's safe as a bare button inside this <form>
                        (type="button" — never submits/saves the template fields). */}
                    <Button
                      type="button"
                      variant="outline"
                      // () => onSyncToday() — NOT onClick={onSyncToday}: React passes the
                      // click SyntheticEvent as the first arg, which would otherwise be
                      // mistaken for the new `force` param (hotfix follow-up, 2026-07-18).
                      onClick={() => onSyncToday()}
                      disabled={syncTodayMutation.isPending}
                      title="Spawn today's round if missing, or refresh it onto the current items if it hasn't started yet."
                    >
                      {syncTodayMutation.isPending ? "Syncing…" : "Sync today's round"}
                    </Button>
                    <Button type="submit" disabled={updateMutation.isPending}>{updateMutation.isPending ? "Saving…" : "Save Checklist"}</Button>
                  </SheetFooter>
                </form>
              </Form>

              <div className="border-t pt-4 space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold">Items ({sortedItems.length})</h3>
                  {/* Phase 42 (TBLD-01) — "New task item" replaces "+ Add Item"; the
                      full task-spec modal below is what this button opens (grep
                      target for this plan's must_haves artifact check). */}
                  <Button size="sm" variant="outline" onClick={openAddItem}>+ New task item</Button>
                </div>
                <p className="text-xs text-muted-foreground">Click any item row to open it in the same modal for editing.</p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[70px]">Order</TableHead>
                      <TableHead>Label</TableHead>
                      <TableHead>Zone</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Required</TableHead>
                      <TableHead className="text-right w-[110px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedItems.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="h-16 text-center text-muted-foreground text-sm">No items yet — add one above.</TableCell></TableRow>
                    ) : (
                      sortedItems.map((it, idx) => (
                        // Phase 42 (TBLD-01) — the whole row opens the New-task-item
                        // modal populated for edit; the per-row action buttons below
                        // stopPropagation so Move/Delete don't also trigger it.
                        <TableRow key={it.id} className="cursor-pointer hover:bg-muted/50" onClick={() => openEditItem(it)}>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <div className="flex flex-col">
                              <Button size="icon" variant="ghost" className="h-5 w-5" disabled={idx === 0 || reorderingItemId === it.id} onClick={() => moveItem(it.id, "up")} aria-label="Move up">
                                <ArrowUp className="h-3 w-3" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-5 w-5" disabled={idx === sortedItems.length - 1 || reorderingItemId === it.id} onClick={() => moveItem(it.id, "down")} aria-label="Move down">
                                <ArrowDown className="h-3 w-3" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            <div className="flex flex-col gap-1">
                              <span>{it.label}</span>
                              {/* Phase 42 (TBLD-01) — compact one-line spec summary
                                  (times · window · validator, proof as a badge). A
                                  legacy item with no spec renders nothing extra. */}
                              {(itemSpecSummaryText(it, users ?? [], usersUnavailable) || (it.proofType && it.proofType !== "none")) && (
                                <div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                                  {itemSpecSummaryText(it, users ?? [], usersUnavailable) && <span>{itemSpecSummaryText(it, users ?? [], usersUnavailable)}</span>}
                                  {it.proofType && it.proofType !== "none" && (
                                    <Badge variant="outline" className="h-4 px-1 py-0 text-[10px] font-normal">{PROOF_TYPE_LABELS[it.proofType as ProofType] ?? it.proofType}</Badge>
                                  )}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {it.zoneLabel}
                            {/* Group-checklist UI (migration 0120) — shown
                                inline so the operator can see at a glance
                                which collapsible header this item lands
                                under on the checklist surfaces. */}
                            {it.groupLabel && <span className="block text-[11px]">{it.groupLabel}</span>}
                          </TableCell>
                          <TableCell className="text-sm">
                            {it.assignedUserId != null ? (
                              <span className="flex flex-col">
                                <span className="font-medium flex items-center gap-1">
                                  <UserRound className="h-3 w-3 text-muted-foreground" />
                                  {usersUnavailable
                                    ? `User #${it.assignedUserId}`
                                    : (users ?? []).find((u) => u.id === it.assignedUserId)?.name ?? `User #${it.assignedUserId}`}
                                </span>
                                <span className="text-xs text-muted-foreground capitalize">Fallback: {it.assignedRole.replace(/_/g, " ")}</span>
                              </span>
                            ) : (
                              <span className="capitalize">{it.assignedRole.replace(/_/g, " ")}</span>
                            )}
                          </TableCell>
                          <TableCell>{it.required ? <Badge variant="secondary">Required</Badge> : <span className="text-xs text-muted-foreground">Optional</span>}</TableCell>
                          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex justify-end items-center gap-1">
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEditItem(it)} aria-label="Edit item"><Pencil className="h-3.5 w-3.5" /></Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => setDeleteItemTarget(it)} aria-label="Delete item"><Trash2 className="h-3.5 w-3.5" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
          {/* Add/Edit item dialog — nested INSIDE the Edit sheet's content so
              the sheet's focus-scope / aria-hidden doesn't disable it (the
              Radix sibling-modal trap: an open modal marks sibling portals
              inert). Its Portal still paints it centered on top; it only
              mounts while the Edit sheet is open. */}
          <Dialog open={itemDialogOpen} onOpenChange={(o) => !o && closeItemDialog()}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            {/* Phase 42 (TBLD-01) — "New task item" / "Edit task item" REPLACES
                the old "Add Item"/"Edit Item" titles; this is the full task-spec
                modal (times · window · proof · doer · validator), not the old
                bare label/zone/role form. */}
            <DialogTitle>{editingItem ? "Edit task item" : "New task item"}</DialogTitle>
            <DialogDescription>Every item is a full task spec — when it runs, how many times, within what window, what proof closes it, who does it, who validates it.</DialogDescription>
          </DialogHeader>
          <Form {...itemForm}>
            <form onSubmit={itemForm.handleSubmit(onItemSubmit)} className="space-y-5">

              {/* ── What ─────────────────────────────────────────────────── */}
              <div className="space-y-3">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">What</h4>
                <FormField name="label" control={itemForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Label <span className="text-destructive">*</span></FormLabel>
                    <FormControl><Input placeholder="e.g. Restock pantry — coffee, tea & water cans" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="zoneLabel" control={itemForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Zone <span className="text-destructive">*</span></FormLabel>
                    <FormControl><Input placeholder="e.g. Pantry · GF, Cabins 1–6, All washrooms" {...field} /></FormControl>
                    <FormDescription>Free text — checklist zones are aggregates, not a single bookable resource.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="zoneResourceId" control={itemForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Linked floor-plan resource (optional)</FormLabel>
                    <Select
                      value={field.value != null ? String(field.value) : "none"}
                      onValueChange={(v) => field.onChange(v === "none" ? null : Number(v))}
                    >
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {(resources ?? []).map((r) => <SelectItem key={r.id} value={String(r.id)}>{r.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                {/* Group-checklist UI (migration 0120) — a REAL ItemSchema
                    field (not a local-state toggle), so plain <FormField>
                    works here. The datalist below suggests group labels
                    already used on THIS template (e.g. "Dusting") without
                    turning it into a closed enum. */}
                <FormField name="groupLabel" control={itemForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Group (optional)</FormLabel>
                    <FormControl>
                      <Input
                        list="checklist-item-group-labels"
                        placeholder="e.g. Dusting"
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value === "" ? null : e.target.value)}
                      />
                    </FormControl>
                    <datalist id="checklist-item-group-labels">
                      {existingGroupLabels.map((g) => <option key={g} value={g} />)}
                    </datalist>
                    <FormDescription>
                      Groups this item under a collapsible header on the checklist (e.g. "Dusting" holds Temple,
                      Sofa, Center Table…). Leave blank to keep this item ungrouped.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="required" control={itemForm.control} render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-md border p-3">
                    <div>
                      <FormLabel>Required</FormLabel>
                      <p className="text-xs text-muted-foreground">Must be checked before the round can be validated (Phase 44).</p>
                    </div>
                    <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                  </FormItem>
                )} />
              </div>

              {/* ── Timing & frequency ──────────────────────────────────── */}
              <div className="space-y-3 border-t pt-4">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Timing &amp; frequency</h4>
                {/* PLAIN markup on purpose — the occurrence-times list is LOCAL
                    STATE (occurrenceTimes), not a react-hook-form field: "times
                    performed" is a UI stepper only, derived server-side as
                    atTimes.length. NOT wrapped in <FormItem>/<FormLabel> — see
                    the repo's standing rule (shadcn Form* needs a <FormField>
                    ancestor) documented on the Pin-to-person Switch below. */}
                <div className="rounded-md border p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium leading-none">Times performed — {occurrenceTimes.length}</label>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setOccurrenceTimes((prev) => [...prev, ""])}
                    >
                      + Add time
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Each time spawns its own independently-tickable occurrence (e.g. 09:00, 13:00, 18:00 for a 3×/day
                    round) — the count above is just a count, not a stored field.
                  </p>
                  {occurrenceTimes.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">No scheduled times — this item runs once per round, whenever the round is worked.</p>
                  ) : (
                    <div className="space-y-2">
                      {occurrenceTimes.map((t, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Input
                            type="time"
                            value={t}
                            onChange={(e) => {
                              const next = [...occurrenceTimes];
                              next[idx] = e.target.value;
                              setOccurrenceTimes(next);
                            }}
                            className="w-40"
                            aria-label={`Occurrence time ${idx + 1}`}
                          />
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => setOccurrenceTimes((prev) => prev.filter((_, i) => i !== idx))}
                            aria-label={`Remove occurrence time ${idx + 1}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <FormField name="completeWithinMinutes" control={itemForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Complete within (minutes)</FormLabel>
                    <FormControl>
                      <Input
                        type="number" min={0} placeholder="e.g. 30"
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value === "" ? null : Number(e.target.value))}
                        className="w-32"
                      />
                    </FormControl>
                    <FormDescription>The doer's clock starts at the occurrence's time-of-day (or round-open, if no time set).</FormDescription>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>

              {/* ── Validation & ownership ──────────────────────────────── */}
              <div className="space-y-3 border-t pt-4">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Validation &amp; ownership</h4>

                {/* PLAIN markup — proofType IS a real ItemSchema field, but its
                    segmented-button selector below is driven by direct
                    itemForm.setValue/watch calls rather than <FormField
                    render>, so (per the standing rule) it stays outside
                    <FormItem>/<FormLabel> too. */}
                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none">Proof to validate</label>
                  <div className="flex flex-wrap gap-2">
                    {PROOF_TYPES.map((pt) => {
                      const active = (itemForm.watch("proofType") ?? "none") === pt;
                      return (
                        <Button
                          key={pt}
                          type="button"
                          size="sm"
                          variant={active ? "default" : "outline"}
                          onClick={() => itemForm.setValue("proofType", pt)}
                        >
                          {PROOF_TYPE_LABELS[pt]}
                        </Button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground">Required proof blocks the doer's tick — they cannot mark done without attaching it.</p>
                </div>

                <FormField name="assignedRole" control={itemForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Doer's role <span className="text-destructive">*</span></FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>{ALL_ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                    </Select>
                    <FormDescription>The default assignee is whoever currently holds this role — stays the fallback even when pinned to a person below.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )} />
                {/* PLAIN markup on purpose — this Switch drives local component
                    state (itemAssignMode), NOT a react-hook-form field, so it has
                    no <FormField> ancestor. Using <FormItem>/<FormLabel> here threw
                    "useFormField should be used within <FormField>" and crashed the
                    whole Add/Edit-item form on open. See the repo's standing rule:
                    shadcn Form* primitives require a FormField ancestor. */}
                <div className="flex items-center justify-between rounded-md border p-3">
                  <div>
                    <label htmlFor="pin-to-person" className="text-sm font-medium leading-none">
                      Pin the doer to a specific person
                    </label>
                    <p className="text-xs text-muted-foreground">
                      {usersUnavailable
                        ? "Person picker unavailable — requires admin access."
                        : "Off = whoever holds the role above. On = this one teammate only, every round."}
                    </p>
                  </div>
                  <Switch
                    id="pin-to-person"
                    checked={itemAssignMode === "person"}
                    disabled={usersUnavailable}
                    onCheckedChange={(checked) => {
                      setItemAssignMode(checked ? "person" : "role");
                      if (!checked) itemForm.setValue("assignedUserId", null);
                    }}
                  />
                </div>
                {itemAssignMode === "person" && (
                  <FormField name="assignedUserId" control={itemForm.control} render={({ field }) => (
                    <FormItem>
                      <FormLabel>Doer <span className="text-destructive">*</span></FormLabel>
                      <AssignedUserCombobox
                        value={field.value ?? null}
                        onChange={field.onChange}
                        users={users ?? []}
                        disabled={usersUnavailable}
                      />
                      <FormDescription>The avatar shown on the checklist will be this teammate, regardless of who currently holds the role.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )} />
                )}

                {/* PLAIN markup — validatorMode is LOCAL, tri-state UI toggle
                    (blank/role/person), not itself a react-hook-form field. Same
                    crash-avoidance reasoning as itemAssignMode above — "blank" is
                    a real, meaningful, and DEFAULT choice here (never pre-select
                    a validator; see the helper text below). */}
                <div className="space-y-2 rounded-md border p-3">
                  <label className="text-sm font-medium leading-none">Who validates &amp; closes it</label>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button" size="sm"
                      variant={validatorMode === "blank" ? "default" : "outline"}
                      onClick={() => {
                        setValidatorMode("blank");
                        itemForm.setValue("validatorRole", null);
                        itemForm.setValue("validatorUserId", null);
                      }}
                    >
                      Doer's manager
                    </Button>
                    <Button
                      type="button" size="sm"
                      variant={validatorMode === "role" ? "default" : "outline"}
                      onClick={() => { setValidatorMode("role"); itemForm.setValue("validatorUserId", null); }}
                    >
                      Specific role
                    </Button>
                    <Button
                      type="button" size="sm"
                      variant={validatorMode === "person" ? "default" : "outline"}
                      disabled={usersUnavailable}
                      onClick={() => { setValidatorMode("person"); itemForm.setValue("validatorRole", null); }}
                    >
                      Specific person
                    </Button>
                  </div>
                  {validatorMode === "blank" && (
                    <p className="text-xs text-muted-foreground">Falls back to the doer's manager. Left blank on purpose — naming one now would freeze today's org chart into the template.</p>
                  )}
                  {validatorMode === "role" && (
                    <FormField name="validatorRole" control={itemForm.control} render={({ field }) => (
                      <FormItem>
                        <FormLabel>Validator role <span className="text-destructive">*</span></FormLabel>
                        <Select value={field.value ?? ""} onValueChange={field.onChange}>
                          <FormControl><SelectTrigger><SelectValue placeholder="Pick a role" /></SelectTrigger></FormControl>
                          <SelectContent>{ALL_ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )} />
                  )}
                  {validatorMode === "person" && (
                    <FormField name="validatorUserId" control={itemForm.control} render={({ field }) => (
                      <FormItem>
                        <FormLabel>Validator <span className="text-destructive">*</span></FormLabel>
                        <AssignedUserCombobox
                          value={field.value ?? null}
                          onChange={field.onChange}
                          users={users ?? []}
                          disabled={usersUnavailable}
                        />
                        <FormMessage />
                      </FormItem>
                    )} />
                  )}
                </div>
              </div>

              <DialogFooter className="mt-4">
                <Button type="button" variant="outline" onClick={closeItemDialog}>Cancel</Button>
                <Button type="submit" disabled={createItemMutation.isPending || updateItemMutation.isPending}>
                  {createItemMutation.isPending || updateItemMutation.isPending ? "Saving…" : "Save task item"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
          </Dialog>

          {/* Delete-item confirm — also nested in the sheet: its trigger is the
              per-item trash button in the items table above, so a sibling
              modal would be inert while the sheet is open. */}
          <AlertDialog open={deleteItemTarget !== null} onOpenChange={(o) => !o && setDeleteItemTarget(null)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove "{deleteItemTarget?.label}"?</AlertDialogTitle>
                <AlertDialogDescription>
                  This is a real delete, not an archive — the item is gone from future rounds immediately. Already-spawned
                  instances (today's, if any) keep their own copy and are unaffected.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={deleteItemMutation.isPending}>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onDeleteItem} disabled={deleteItemMutation.isPending} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  {deleteItemMutation.isPending ? "Removing…" : "Remove"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Hotfix follow-up (2026-07-18, "force resync") — also nested in the sheet
              (triggered by a 409 from the "Sync today's round" button above, scoped to
              editTarget). Plain markup, mirrors the delete-item confirm right above —
              NOT a FormField (this repo's shadcn Form* components white-screen outside
              a FormField ancestor). */}
          <AlertDialog open={syncForceConfirmOpen} onOpenChange={(o) => !o && setSyncForceConfirmOpen(false)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Today's round already has progress</AlertDialogTitle>
                <AlertDialogDescription>
                  Resyncing will DISCARD today's ticks/validations and rebuild this round from the current items.
                  Continue?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={syncTodayMutation.isPending}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={onForceSyncToday}
                  disabled={syncTodayMutation.isPending}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {syncTodayMutation.isPending ? "Resyncing…" : "Discard & Resync"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </SheetContent>
      </Sheet>

      <AlertDialog open={archiveTarget !== null} onOpenChange={(o) => !o && setArchiveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive "{archiveTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Stops future spawns only — today's already-spawned round (if any) stays visible and workable.
              Reversible from Edit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiveMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onArchive} disabled={archiveMutation.isPending} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {archiveMutation.isPending ? "Archiving…" : "Archive"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Hotfix (permanent Delete) — a REAL delete, distinct from Archive above.
          Plain markup (not a FormField — this repo's shadcn Form* components
          white-screen outside a FormField ancestor); mirrors the delete-item
          confirm's own AlertDialog pattern. Copy is deliberately specific and
          honest about what survives, using the counts already loaded on this
          page (items.length + spawnedRoundCount from the templates list read)
          — no extra fetch needed to open this dialog. */}
      <AlertDialog open={permanentDeleteTarget !== null} onOpenChange={(o) => !o && setPermanentDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{permanentDeleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This is a real delete, not an archive. Its {permanentDeleteTarget?.items.length ?? 0} item
              {(permanentDeleteTarget?.items.length ?? 0) === 1 ? "" : "s"} are removed.{" "}
              {permanentDeleteTarget?.spawnedRoundCount ?? 0} past round
              {(permanentDeleteTarget?.spawnedRoundCount ?? 0) === 1 ? "" : "s"} are KEPT for the record but will no
              longer be linked to this checklist. Archive instead if you only want it to stop running.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={permanentDeleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onPermanentDelete}
              disabled={permanentDeleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {permanentDeleteMutation.isPending ? "Deleting…" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}
